package com.oscarherreros.appcalculo.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin
import kotlin.random.Random

// Los mismos valores que la ráfaga de la app (ui/Confetti.kt).
private const val PARTICLES = 70
private const val DURATION_MILLIS = 1500.0

/** Cuánto cae un trozo por la gravedad al final de la ráfaga, en alturas de la pantalla. */
private const val FALL = 0.55

/** Un trozo de confeti: sale del origen en dirección [angle] y gira sobre sí mismo. */
private class Particle(
    val angle: Double,
    val reach: Double,
    val turns: Double,
    val scale: Double,
    val color: String,
)

/**
 * Ráfaga de confeti al resolver el reto, como en la app: sale del centro de la
 * pantalla hacia arriba y hacia los lados, por encima de la tarjeta, y el
 * lienzo se quita al acabar. No se lanza si el sistema pide reducir el movimiento.
 */
fun confettiBurst() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    val canvas = document.body?.child<HTMLCanvasElement>("canvas", "confetti") ?: return
    canvas.setAttribute("aria-hidden", "true")
    val width = canvas.clientWidth.toDouble()
    val height = canvas.clientHeight.toDouble()
    val ratio = window.devicePixelRatio
    canvas.width = (width * ratio).toInt()
    canvas.height = (height * ratio).toInt()
    val context = canvas.getContext("2d") as CanvasRenderingContext2D
    context.scale(ratio, ratio)

    // Los colores del tema que use la página (claro u oscuro), como en la app.
    val style = window.getComputedStyle(document.documentElement!!)
    val palette = listOf("--primary", "--tertiary", "--error", "--star").map { style.getPropertyValue(it).trim() }
    val particles = List(PARTICLES) {
        Particle(
            // Hacia arriba y hacia los lados: de 200° a 340°.
            angle = (200 + Random.nextDouble() * 140) * PI / 180,
            reach = 0.25 + Random.nextDouble() * 0.6,
            turns = Random.nextDouble() * 4 - 2,
            scale = 0.7 + Random.nextDouble() * 0.6,
            color = palette.random(),
        )
    }

    var start = -1.0
    fun frame(now: Double) {
        if (start < 0) start = now
        val time = (now - start) / DURATION_MILLIS
        context.clearRect(0.0, 0.0, width, height)
        if (time >= 1) {
            canvas.remove()
            return
        }
        // Sale deprisa y se va frenando, mientras la gravedad lo hace caer.
        val travelled = 1 - (1 - time) * (1 - time)
        context.globalAlpha = (1 - time * time).coerceIn(0.0, 1.0)
        particles.forEach { particle ->
            val distance = particle.reach * width * travelled
            val x = width / 2 + cos(particle.angle) * distance
            val y = height * 0.45 + sin(particle.angle) * distance + FALL * height * time * time
            val pieceWidth = 10 * particle.scale
            val pieceHeight = 6 * particle.scale
            context.save()
            context.translate(x, y)
            context.rotate(particle.turns * 2 * PI * time)
            context.fillStyle = particle.color
            context.fillRect(-pieceWidth / 2, -pieceHeight / 2, pieceWidth, pieceHeight)
            context.restore()
        }
        window.requestAnimationFrame(::frame)
    }
    window.requestAnimationFrame(::frame)
}
